import React from 'react';
import { Box, Typography, Alert, AlertTitle, Button, CircularProgress, List, ListItem, ListItemIcon, ListItemText } from '@mui/material';
import { CheckCircle, Error as ErrorIcon, Warning } from '@mui/icons-material';

function statusIcon(status) {
  if (status === 'pass') return <CheckCircle fontSize="small" color="success" />;
  if (status === 'warn') return <Warning fontSize="small" color="warning" />;
  return <ErrorIcon fontSize="small" color="error" />;
}

/**
 * Presentational diagnostics panel.
 *
 * Props:
 *   running  {boolean}        a check is in progress
 *   report   {object|null}    result of runConnectDiagnostics(): { results, overall, summary }
 *   onRun    {function}       trigger (re)running the checks
 *   compact  {boolean}        hide the intro line (used inside the stuck-spinner view)
 */
export default function ConnectDiagnostics({ running, report, onRun, compact = false }) {
  return (
    <Box sx={{ textAlign: 'left', width: '100%' }}>
      {!compact && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          Run a quick check to find what's blocking the connection on this device.
        </Typography>
      )}

      {!report && (
        <Button
          variant="outlined"
          size="small"
          onClick={onRun}
          disabled={running}
          startIcon={running ? <CircularProgress size={16} /> : null}
        >
          {running ? 'Checking your connection…' : 'Run connection check'}
        </Button>
      )}

      {report && (
        <Box>
          {/* Most likely culprit first */}
          {report.summary ? (
            <Alert severity="error" sx={{ mb: 2 }}>
              <AlertTitle>Likely cause: {report.summary.label}</AlertTitle>
              {report.summary.advice || report.summary.detail}
            </Alert>
          ) : report.overall === 'warn' ? (
            <Alert severity="warning" sx={{ mb: 2 }}>
              <AlertTitle>Possible issue found</AlertTitle>
              Review the warnings below — they can interfere with sign-in.
            </Alert>
          ) : (
            <Alert severity="success" sx={{ mb: 2 }}>
              <AlertTitle>No blocking issues found</AlertTitle>
              Your device and network can reach our services. If it still won't connect,
              this is likely temporary — please try again, or contact support.
            </Alert>
          )}

          <List dense disablePadding>
            {report.results.map((r) => (
              <ListItem key={r.id} alignItems="flex-start" sx={{ px: 0 }}>
                <ListItemIcon sx={{ minWidth: 32, mt: 0.5 }}>{statusIcon(r.status)}</ListItemIcon>
                <ListItemText
                  primary={r.label}
                  secondary={
                    <>
                      {r.detail}
                      {r.status !== 'pass' && r.advice ? (
                        <Typography component="span" variant="caption" display="block" color="text.secondary" sx={{ mt: 0.5 }}>
                          {r.advice}
                        </Typography>
                      ) : null}
                    </>
                  }
                  primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
                />
              </ListItem>
            ))}
          </List>

          <Button variant="text" size="small" onClick={onRun} disabled={running} sx={{ mt: 1 }}>
            {running ? 'Re-checking…' : 'Run check again'}
          </Button>
        </Box>
      )}
    </Box>
  );
}
